import React, { ForwardedRef } from 'react';
import clsx from 'clsx';
import Tailwindcss from '@/utils/tailwind';

interface CellProp extends React.HTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
  colSpan?: number;
  rowSpan?: number;
  className?: string;
  style?: React.CSSProperties;
}

interface TrProp {
  children?: React.ReactNode;
  className?: string;
  onClick?: (ev: React.MouseEvent<HTMLTableRowElement>) => void;
  onDoubleClick?: (ev: React.MouseEvent<HTMLTableRowElement>) => void;
  noHover?: boolean;
  role?: string;
}

const Thead = ({
  children,
  ...props
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}): JSX.Element => <thead {...props}>{children}</thead>;
const Tbody = ({ children }: { children?: React.ReactNode }): JSX.Element => (
  <tbody>{children}</tbody>
);
const Th = ({ children, className, colSpan, rowSpan, style }: CellProp): JSX.Element => {
  const newClassName = Tailwindcss.mergeClasses(
    `py-2 px-2 group/table text-xs text-start bg-[#112757] text-white`,
    className ?? '',
  );
  return (
    <th
      colSpan={colSpan}
      rowSpan={rowSpan}
      className={clsx(newClassName, 'first-of-type:rounded-ss last-of-type:rounded-se')}
      style={style}
    >
      {children}
    </th>
  );
};
const Td = ({ children, className, colSpan, rowSpan, style }: CellProp): JSX.Element => (
  <td
    colSpan={colSpan}
    rowSpan={rowSpan}
    className={clsx('px-2 py-1.5 text-xs', className)}
    style={style}
  >
    {children}
  </td>
);
const Tr = React.forwardRef(
  (
    {
      children,
      className,
      onClick = () => {},
      onDoubleClick = () => {},
      noHover = false,
      ...props
    }: TrProp,
    ref: ForwardedRef<any>,
  ): JSX.Element => (
    <tr
      ref={ref}
      onDoubleClick={onDoubleClick}
      className={clsx(className, 'border-b dark:border-zinc-700', noHover ? '' : 'component')}
      onClick={onClick}
      {...props}
    >
      {children}
    </tr>
  ),
);
const Table = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}): JSX.Element => {
  return (
    <div className={clsx('relative mt-0 max-w-screen-sm overflow-auto sm:max-w-full', className)}>
      <table className="w-full">{children}</table>
    </div>
  );
};

export default {
  Thead,
  Tbody,
  Th,
  Td,
  Tr,
  Table,
};
